import { supabase } from './supabase';

/**
 * Client-laag voor POST /api/customers/verhuizing.
 *
 * Onderscheid met `applyAddressOverride` in orders.ts: die is order-scoped en
 * eenmalig, dit is klant-scoped. De reikwijdte is een keuze van de medewerker,
 * geen afleiding uit de data — daarom twee aparte verbs op één dialoog.
 */

export interface VerhuisAdres {
  street: string;
  postcode: string;
  city: string;
  toevoeging?: string | null;
}

export type OphaalActie = 'pinnen' | 'meeverhuizen' | 'al-gepind' | 'onvolledig';

export interface VerhuisOrderPlan {
  woId: string;
  naam: string;
  status: string;
  opgehaald: boolean;
  ophaalRitGepland: boolean;
  ophaalActie: OphaalActie;
  ophaalAdres: VerhuisAdres | null;
  afleverActie: 'meeverhuizen' | 'al-goed';
  toelichting: string;
}

export interface VerhuisPlan {
  accountId: string;
  huidigAdres: VerhuisAdres | null;
  nieuwAdres: VerhuisAdres;
  orders: VerhuisOrderPlan[];
  blokkades: string[];
}

export interface VerhuisOrderUitkomst {
  woId: string;
  naam: string;
  ophaalActie: OphaalActie;
  ok: boolean;
  fout?: string;
  voorstellenGewist?: boolean;
}

export interface VerhuisResultaat {
  accountBijgewerkt: boolean;
  orders: VerhuisOrderUitkomst[];
  plan: VerhuisPlan;
}

export class VerhuisError extends Error {
  code?: string;
  orders?: VerhuisOrderUitkomst[];
  accountBijgewerkt?: boolean;

  constructor(message: string, extra?: { code?: string; orders?: VerhuisOrderUitkomst[]; accountBijgewerkt?: boolean }) {
    super(message);
    this.name = 'VerhuisError';
    this.code = extra?.code;
    this.orders = extra?.orders;
    this.accountBijgewerkt = extra?.accountBijgewerkt;
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
  return headers;
}

async function post(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const response = await fetch('/api/customers/verhuizing', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new VerhuisError((payload.error as string) || 'Verhuizing mislukt', {
      code: payload.code as string | undefined,
      orders: payload.orders as VerhuisOrderUitkomst[] | undefined,
      accountBijgewerkt: payload.accountBijgewerkt as boolean | undefined,
    });
  }
  return payload;
}

/** Stelt het plan op zonder te schrijven: welke orders raakt deze verhuizing? */
export async function previewVerhuizing(accountId: string, adres: VerhuisAdres): Promise<VerhuisPlan> {
  const payload = await post({ accountId, adres, dryRun: true });
  return payload.plan as VerhuisPlan;
}

export async function applyVerhuizing(accountId: string, adres: VerhuisAdres): Promise<VerhuisResultaat> {
  const payload = await post({ accountId, adres });
  return {
    accountBijgewerkt: !!payload.accountBijgewerkt,
    orders: (payload.orders as VerhuisOrderUitkomst[]) ?? [],
    plan: payload.plan as VerhuisPlan,
  };
}
